/**
 * Settings — Appearance.
 *
 * Two kinds of choice, and the difference matters for where each is kept.
 *
 * The palette and light/dark live in localStorage via ThemeProvider and apply through
 * the CSS custom properties in styles.css, so they follow the browser rather than the
 * account and every role may set them.
 *
 * The organisation's name is the other kind: one answer for everybody who opens the
 * archive, held on the server, and the owner's alone to change. It sits here because
 * this is the page about what the archive looks like, and the name beside the mark is
 * the most visible thing on it — but it is not a preference, and the card says so.
 */
import { useState } from 'react'
import { PageHeader } from '../App'
import { useTheme, SCHEMES, MODES } from '../lib/theme'
import { useMe, api, useApiMutation } from '../lib/api'
import { Card, Field, useToast } from '../components/ui'
import { DEFAULT_TERM } from '../lib/terms'
import { Icon } from '../components/Icon'

const ORG_NAME_MAX = 60

/** Mirrors PARTNER_TERM_MAX in src/worker/util.js, which is what actually refuses. */
const TERM_MAX = 32

/** Mirrors CODE_PREFIX_MAX in src/worker/util.js, likewise. */
const CODE_PREFIX_MAX = 8

/* The three modes and their glyphs come from theme.jsx, which the header's toggle
   also draws from, so the tile you pick here is the icon you then see up there —
   and clicking that icon steps through these same three. */
const HINTS = {
  light: 'Always the light palette.',
  dark: 'Always the dark palette.',
  system: 'Follow the light/dark setting of this device.',
}

/**
 * Rename the archive.
 *
 * Only rendered for an owner. An administrator can configure how this application
 * works and still not decide what it is called, which is the same line the Worker
 * draws — it gates the write on `manageUsers`, so hiding the card is a courtesy
 * rather than the control.
 *
 * Saving invalidates `me`, because that is where the name reaches every page: the
 * sidebar redraws from the same request that authenticated it, with no reload.
 */
function OrgNameCard({ current, fallback }) {
  const [value, setValue] = useState(current ?? '')
  const [saved, setSaved] = useState(current ?? '')
  const toast = useToast()

  // The field follows the server when the saved name changes underneath it — after a
  // save, or when another owner renamed it while this page was open. Adjusted while
  // rendering rather than in an effect, so the input is never painted once with the
  // old name and again with the new one.
  if (saved !== (current ?? '')) {
    setSaved(current ?? '')
    setValue(current ?? '')
  }

  const save = useApiMutation(
    (org_name) => api('settings/app', { method: 'PUT', body: { org_name } }),
    ['me']
  )

  const trimmed = value.replace(/\s+/g, ' ').trim()
  const tooLong = trimmed.length > ORG_NAME_MAX
  const hasMarkup = /[<>]/.test(trimmed)
  const dirty = trimmed !== (current ?? '')
  const error = tooLong
    ? `Keep it to ${ORG_NAME_MAX} characters or fewer.`
    : hasMarkup
      ? 'The name cannot contain < or >.'
      : null

  const submit = (e) => {
    e.preventDefault()
    if (error || !dirty) return
    save.mutate(trimmed, {
      onSuccess: (r) => toast.ok(`Now called “${r.orgName}”.`),
      onError: (err) => toast.error(err),
    })
  }

  return (
    <Card
      className="appearance-card"
      title="Organization Name"
      sub="Shown beside the mark at the top of the sidebar, and on both sign-in screens. This one is saved for everybody, not just this browser."
    >
      <form className="col" onSubmit={submit}>
        <Field
          label="Name"
          htmlFor="org-name"
          error={error}
          hint={`Leave it empty to go back to “${fallback}”. ${ORG_NAME_MAX - trimmed.length} characters left.`}
        >
          <input
            id="org-name"
            className="input"
            value={value}
            maxLength={ORG_NAME_MAX + 10}
            placeholder={fallback}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="organization"
          />
        </Field>
        <div className="row">
          <button className="btn btn-primary" type="submit" disabled={!dirty || !!error || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save name'}
          </button>
          {dirty && (
            <button className="btn" type="button" onClick={() => setValue(current ?? '')}>
              Cancel
            </button>
          )}
        </div>
        <p className="small subtle" style={{ margin: 0 }}>
          This does not change the address the archive sends email from — that is
          <code> MAIL_FROM</code> in the Worker configuration.
        </p>
      </form>
    </Card>
  )
}

/**
 * The four letters in front of every code.
 *
 * Every partner unit is identified by a code — S4CA-030 — and those four letters are this
 * organisation's, not the application's. Set them here and every code the archive draws
 * reads the same way: the directory, the cards, the reports, the exported spreadsheets,
 * the placeholder in the code field.
 *
 * WHAT IT DOES NOT DO, and the card says so, because somebody about to change this
 * deserves to know. No stored code is rewritten. The column keeps the codes the registry
 * export gave it, which is what keeps four hundred rows, every photograph named after a
 * unit, every Kindful campaign and the text of five thousand updates all still pointing at
 * the same records — and is why setting it back puts everything as it was. The importers
 * accept a file written either way.
 */
function CodePrefixCard({ current, fallback, max }) {
  const [saved, setSaved] = useState(current ?? '')
  const [value, setValue] = useState(current ?? '')
  const toast = useToast()

  // Follows the server when it changes underneath, adjusted while rendering — the same
  // reasoning as the name above.
  if (saved !== (current ?? '')) {
    setSaved(current ?? '')
    setValue(current ?? '')
  }

  const clean = value.replace(/\s+/g, '').toUpperCase()
  const isDefault = clean === fallback || clean === ''
  const error =
    clean.length > max
      ? `Keep it to ${max} characters or fewer.`
      : clean && clean.length < 2
        ? 'Give at least two characters.'
        : clean && !/^[A-Z0-9]+$/.test(clean)
          ? 'Letters and digits only — no spaces, hyphens or punctuation.'
          : clean && !/[A-Z]/.test(clean)
            ? 'Include at least one letter.'
            : null
  const dirty = clean !== (current ?? '')

  const save = useApiMutation((prefix) => api('settings/code-prefix', { method: 'PUT', body: { prefix } }), ['me'])

  const submit = (e) => {
    e.preventDefault()
    if (error || !dirty) return
    save.mutate(clean, {
      onSuccess: (r) => toast.ok(`Codes now read ${r.partnerTerm.codePrefix}-000.`),
      onError: (err) => toast.error(err),
    })
  }

  return (
    <Card
      className="appearance-card"
      title="Code Prefix"
      sub="The letters in front of every partner unit's code. Saved for everybody, like the name above."
    >
      <form className="col" onSubmit={submit}>
        {/* The hint says nothing about the default on purpose.
            A Field's hint is authored text and goes through the renaming — including this
            feature's own pass, which rewrites the four letters wherever it finds them. So a
            hint reading "leave it empty to go back to S4CA" came out as "go back to ABCX",
            offering the value already in the box. Everything this card has to say about the
            default lives in the paragraph below instead, which is a plain element and is
            not rewritten. */}
        <Field label="Prefix" htmlFor="code-prefix" error={error} hint="Two to eight letters or digits.">
          <input
            id="code-prefix"
            className="input mono"
            style={{ maxWidth: 180, textTransform: 'uppercase' }}
            value={value}
            maxLength={max + 4}
            placeholder={fallback}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <div className="row">
          <button className="btn btn-primary" type="submit" disabled={!dirty || !!error || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save prefix'}
          </button>
          {dirty && (
            <button className="btn" type="button" onClick={() => setValue(current ?? '')}>
              Cancel
            </button>
          )}
        </div>
        <p className="small subtle" style={{ margin: 0 }}>
          {`Codes will read ${(!error && clean) || fallback}-030. Leave the box empty to go back to ${fallback}. `}
          {isDefault
            ? `They are stored as ${fallback}-030 either way: this changes only what is drawn in front of the number, no record is rewritten, and imports go on accepting files written either way.`
            : `They are still stored as ${fallback}-030 — this changes only what the archive draws in front of the number. Nothing is rewritten, so setting it back to ${fallback} puts every page as it was, and an import naming ${clean}-030 or ${fallback}-030 reaches the same unit either way.`}
        </p>
      </form>
    </Card>
  )
}

/**
 * What this installation calls a partner unit.
 *
 * The archive's records are partner units and every label in the application says so;
 * an organisation that calls them staff units or affiliate units sets the word here and
 * it is used everywhere the old one was — sidebar, page titles, table columns, tooltips,
 * exported CSV headings, even the Worker's own error messages. How that works is the
 * head of src/app/lib/terms.jsx.
 *
 * Two fields rather than one and a rule for making plurals, because English has no such
 * rule that survives contact with real nouns, and the word is needed in both numbers.
 * Both or neither: half a term would rename the singular everywhere and leave the plural
 * as it was, which reads as a fault rather than as a setting — the Worker refuses it and
 * the button below stays disabled rather than letting somebody find that out.
 *
 * Beside the organisation's name because it is the same kind of setting: one answer for
 * everybody who opens the archive, held on the server, and the owner's alone to change.
 */
function PartnerTermCard({ current }) {
  const blank = (v, dflt) => (v === dflt ? '' : v ?? '')
  const [one, setOne] = useState(blank(current.one, DEFAULT_TERM.one))
  const [many, setMany] = useState(blank(current.many, DEFAULT_TERM.many))
  const [pOne, setPOne] = useState(blank(current.personOne, DEFAULT_TERM.personOne))
  const [pMany, setPMany] = useState(blank(current.personMany, DEFAULT_TERM.personMany))
  const [saved, setSaved] = useState(current)
  const toast = useToast()

  // Follows the server when either term changes underneath the form — after a save, or
  // when another owner set one while this page was open. Adjusted while rendering, so
  // the inputs are never painted once with the old word and again with the new.
  if (
    saved.one !== current.one ||
    saved.many !== current.many ||
    saved.personOne !== current.personOne ||
    saved.personMany !== current.personMany
  ) {
    setSaved(current)
    setOne(blank(current.one, DEFAULT_TERM.one))
    setMany(blank(current.many, DEFAULT_TERM.many))
    setPOne(blank(current.personOne, DEFAULT_TERM.personOne))
    setPMany(blank(current.personMany, DEFAULT_TERM.personMany))
  }

  const save = useApiMutation(
    (body) => api('settings/partner-term', { method: 'PUT', body }),
    ['me']
  )

  const clean = (v) => v.replace(/\s+/g, ' ').trim()
  const cOne = clean(one)
  const cMany = clean(many)
  const cPOne = clean(pOne)
  const cPMany = clean(pMany)

  /* Each pair is judged on its own: renaming the records does not oblige anybody to rename
     the people in them, and either can go back to the default while the other stays. The
     one rule inside a pair is both halves or neither. */
  const halfPair = Boolean(cOne) !== Boolean(cMany) || Boolean(cPOne) !== Boolean(cPMany)
  const all = [cOne, cMany, cPOne, cPMany]
  const tooLong = all.some((v) => v.length > TERM_MAX)
  const hasMarkup = all.some((v) => /[<>]/.test(v))
  const error = halfPair
    ? 'Fill in both halves of a pair, or clear both to go back to the default.'
    : tooLong
      ? `Keep each one to ${TERM_MAX} characters or fewer.`
      : hasMarkup
        ? 'The name cannot contain < or >.'
        : null
  const dirty =
    (cOne || DEFAULT_TERM.one) !== current.one ||
    (cMany || DEFAULT_TERM.many) !== current.many ||
    (cPOne || DEFAULT_TERM.personOne) !== (current.personOne ?? DEFAULT_TERM.personOne) ||
    (cPMany || DEFAULT_TERM.personMany) !== (current.personMany ?? DEFAULT_TERM.personMany)

  const submit = (e) => {
    e.preventDefault()
    if (error || !dirty) return
    save.mutate(
      { one: cOne, many: cMany, person_one: cPOne, person_many: cPMany },
      {
        onSuccess: (r) =>
          toast.ok(
            `Now “${r.partnerTerm.one}” and “${r.partnerTerm.many}”, and ` +
              `“${r.partnerTerm.personOne}” and “${r.partnerTerm.personMany}”.`
          ),
        onError: (err) => toast.error(err),
      }
    )
  }

  return (
    <Card
      className="appearance-card"
      /* Deliberately says none of the words it sets. A card title is relabelled like
         every other label, so a title carrying the default term would rename itself the
         moment somebody used this card — "What to call a Staff Unit" is a riddle. */
      title="What These Records Are Called"
      sub="Two words this archive uses everywhere — in the sidebar, in every page title and table column, and in the files it exports. Saved for everybody, not just this browser."
    >
      <form className="col" onSubmit={submit}>
        {/* A card's children are left alone by the renaming, which is what lets these two
            headings name the things they are about. */}
        <p className="term-group">The record</p>
        <Field label="One of them" htmlFor="term-one">
          <input
            id="term-one"
            className="input"
            value={one}
            maxLength={TERM_MAX + 10}
            placeholder={DEFAULT_TERM.one}
            onChange={(e) => setOne(e.target.value)}
          />
        </Field>
        <Field label="More than one" htmlFor="term-many">
          <input
            id="term-many"
            className="input"
            value={many}
            maxLength={TERM_MAX + 10}
            placeholder={DEFAULT_TERM.many}
            onChange={(e) => setMany(e.target.value)}
          />
        </Field>

        <p className="term-group">One of its people</p>
        <Field label="One of them" htmlFor="person-one">
          <input
            id="person-one"
            className="input"
            value={pOne}
            maxLength={TERM_MAX + 10}
            placeholder={DEFAULT_TERM.personOne}
            onChange={(e) => setPOne(e.target.value)}
          />
        </Field>
        <Field label="More than one" htmlFor="person-many" error={error}>
          <input
            id="person-many"
            className="input"
            value={pMany}
            maxLength={TERM_MAX + 10}
            placeholder={DEFAULT_TERM.personMany}
            onChange={(e) => setPMany(e.target.value)}
          />
        </Field>

        {/* Not a Field's own hint and not a placeholder either: a hint is relabelled like
            any other label, and these sentences have to be able to say the default words
            without them being swapped for the ones being offered. A card's children are
            left alone, which is what makes this the place for them. */}
        <p className="small subtle" style={{ margin: 0 }}>
          A record is the unit this archive keeps — the thing with a code, a country and a
          history of updates. Its people are who that unit is made of: one on their own, or
          two where a married couple serve together.
        </p>
        <p className="small subtle" style={{ margin: 0 }}>
          <strong>Partner</strong>, <strong>Staff</strong> and <strong>Affiliate</strong> are
          the usual three, and which of them fits is a question about the relationship
          between this organization and the people in the archive rather than about the
          archive itself: they may be staff you send and support, partners you walk
          alongside as equals, or affiliates connected to you without either. Use whichever
          is true here. Either pair can be left alone while the other is changed.
        </p>
        <p className="small subtle" style={{ margin: 0 }}>
          Leave a pair empty to go back to “{DEFAULT_TERM.one}” and “{DEFAULT_TERM.many}”, or
          to “{DEFAULT_TERM.personOne}” and “{DEFAULT_TERM.personMany}”.
        </p>
        <div className="row">
          <button className="btn btn-primary" type="submit" disabled={!dirty || !!error || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save wording'}
          </button>
          {dirty && (
            <button
              className="btn"
              type="button"
              onClick={() => {
                setOne(current.one === DEFAULT_TERM.one ? '' : current.one)
                setMany(current.many === DEFAULT_TERM.many ? '' : current.many)
              }}
            >
              Cancel
            </button>
          )}
        </div>
        <p className="small subtle" style={{ margin: 0 }}>
          {/* Said out loud because it is the one thing this setting deliberately does
              not do: an update that used the old word still uses it, because the archive
              holds what was written rather than a version of it. */}
          This renames the labels the application draws. It does not rewrite the archive:
          an update or a description that says “partner unit” keeps saying it.
        </p>
      </form>
    </Card>
  )
}

export default function SettingsAppearance() {
  const { pref, setPref, resolved, scheme, setScheme } = useTheme()
  const active = SCHEMES.find((s) => s.key === scheme)
  const me = useMe()
  const canRename = Boolean(me.data?.caps?.manageUsers)

  return (
    <PageHeader title="Appearance" sub="Colors and light/dark for this browser">
      <div className="grid">
        {canRename && (
          <OrgNameCard
            current={me.data?.orgName ?? ''}
            fallback={me.data?.appName || 's4narchive'}
          />
        )}
        {canRename && (
          <CodePrefixCard
            current={
              (me.data?.partnerTerm?.codePrefix ?? DEFAULT_TERM.codePrefix) === DEFAULT_TERM.codePrefix
                ? ''
                : me.data.partnerTerm.codePrefix
            }
            fallback={DEFAULT_TERM.codePrefix}
            max={CODE_PREFIX_MAX}
          />
        )}
        {canRename && <PartnerTermCard current={me.data?.partnerTerm ?? DEFAULT_TERM} />}
        <Card
          className="appearance-card"
          title="Color Scheme"
          sub="Pick the brand palette. It is applied through CSS variables, so it works in both light and dark mode. Your choice is saved on this device."
        >
          <div className="scheme-grid">
            {SCHEMES.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`scheme-tile${s.key === scheme ? ' active' : ''}`}
                onClick={() => setScheme(s.key)}
                aria-pressed={s.key === scheme}
              >
                <span className="scheme-dot" style={{ background: s.dot }} aria-hidden="true" />
                <span className="scheme-name">{s.label}</span>
                {s.key === scheme && (
                  <span className="scheme-check" aria-hidden="true">
                    ✓
                  </span>
                )}
              </button>
            ))}
          </div>
        </Card>

        <Card
          className="appearance-card"
          title="Appearance"
          sub="Also on the appearance icon at the top right of every page, which steps through these three."
        >
          <div className="mode-grid">
            {MODES.map(({ key, icon, label }) => (
              <button
                key={key}
                type="button"
                className={`btn btn-lg mode-tile${pref === key ? ' active' : ''}`}
                onClick={() => setPref(key)}
                title={HINTS[key]}
                aria-pressed={pref === key}
              >
                <Icon name={icon} size={20} />
                <span>{label}</span>
              </button>
            ))}
          </div>
          <p className="small subtle mode-note">
            {pref === 'system'
              ? `Following this device, which is currently set to ${resolved}.`
              : `Fixed to ${pref}, whatever this device is set to.`}{' '}
            The {active?.label.toLowerCase()} palette applies either way.
          </p>
        </Card>
      </div>
    </PageHeader>
  )
}
